import { useCaseFilterStore } from '@store/caseFilter';
import { useFilterStore } from '@store/filter';
import { useMeetingFilterStore } from '@store/meetingFilter';
import { useProspectiveSiteFilterStore } from '@store/prospectiveSiteFilter';
import { useOneToOneMeetingFilterStore } from '@store/oneToOneFilter';
import { TYPE_OF_DISCUSSION_OPTIONS } from '@config/meetingConstant';
import { useDFCCClubFilterStore } from '@store/DFCCClubFilter';
import { useSelectClubMemberFilterStore } from '@store/selectClubMemberFilter';
import { useSiteFilterStore } from '@store/siteFilter';
import { useProductSamplingStore } from '@store/productSamplingStore';
import { isMeetingModulePath } from '@helper/meetingModulePaths';

export type RouteResetContext = {
	currentPath: string;
	previousPath: string | null;
};

type RouteMatcher = (path: string) => boolean;

const isCaseRoute: RouteMatcher = (path) => path.startsWith('/case-module') || path.startsWith('/case-module-details');

const oneToOneDiscussionRoutes = TYPE_OF_DISCUSSION_OPTIONS.map((option) => option.redirectTo).filter(
	(route): route is string => Boolean(route)
);

const isUsersRoute: RouteMatcher = (path) =>
	path.startsWith('/leads') ||
	path.startsWith('/end-users') ||
	path.startsWith('/lead/') ||
	path.startsWith('/contractor');

const isOneToOneMeetingRoute: RouteMatcher = (path) =>
	path.startsWith('/one-to-one-meeting') || oneToOneDiscussionRoutes.some((route) => path.startsWith(route));

const isMeetingsRoute: RouteMatcher = (path) => isMeetingModulePath(path);

const isDfccRoute: RouteMatcher = (path) => path.startsWith('/dfcc-clubs');
const isSelectClubMemberRoute: RouteMatcher = (path) => path.startsWith('/dfcc-clubs/select-club-member');

const isProspectiveSiteRoute: RouteMatcher = (path) =>
	path.startsWith('/prospective-site') || path.startsWith('/add-prospective-site') || path.startsWith('/add-new-site');

const isSiteRoute: RouteMatcher = (path) => path.startsWith('/sites') || path.startsWith('/site-details-rr');

/** Product Sampling + related add/detail routes where Site 360 return state should stay available. */
const isProductSamplingModuleRoute: RouteMatcher = (path) =>
	path.startsWith('/product-sampling-list') ||
	path.startsWith('/product-sampling-add') ||
	path.startsWith('/product-sampling-detail') ||
	path.startsWith('/raincoat-preview-add') ||
	path.startsWith('/raincoat-preview-detail');

export type RouteResetRule = {
	id: string;
	shouldRun: (context: RouteResetContext) => boolean;
	run: (context: RouteResetContext) => void;
};

export const routeResetRules: RouteResetRule[] = [
	{
		id: 'users-store-reset',
		shouldRun: ({ currentPath }): boolean => !isUsersRoute(currentPath),
		run: (): void => {
			useFilterStore.getState().resetFilterStore();
		},
	},
	{
		id: 'case-store-reset',
		shouldRun: ({ currentPath }): boolean => !isCaseRoute(currentPath),
		run: (): void => {
			useCaseFilterStore.getState().resetCaseFilterStore();
		},
	},
	{
		id: 'one-to-one-meeting-store-reset',
		shouldRun: ({ currentPath }): boolean => !isOneToOneMeetingRoute(currentPath),
		run: (): void => {
			useOneToOneMeetingFilterStore.getState().resetOneToOneMeetingFilterStore();
		},
	},
	{
		id: 'prospective-site-filter-reset',
		shouldRun: ({ currentPath }): boolean => !isProspectiveSiteRoute(currentPath),
		run: (): void => {
			useProspectiveSiteFilterStore.getState().resetProspectiveSiteFilterStore();
		},
	},
	{
		id: 'site-filter-reset',
		shouldRun: ({ currentPath }): boolean => !isSiteRoute(currentPath),
		run: (): void => {
			useSiteFilterStore.getState().resetSiteFilterStore();
		},
	},
	{
		id: 'product-sampling-site360-return-clear',
		shouldRun: ({ currentPath }): boolean => !isProductSamplingModuleRoute(currentPath),
		run: (): void => {
			useProductSamplingStore.getState().setSite360ReturnState(null);
		},
	},
	{
		id: 'meeting-filter-reset',
		shouldRun: ({ currentPath, previousPath }): boolean =>
			previousPath !== null && isMeetingsRoute(currentPath) && !isMeetingsRoute(previousPath),
		run: ({ previousPath }): void => {
			if (!previousPath) return;
			useMeetingFilterStore.getState().resetMeetingFilterStore();
		},
	},
	{
		id: 'dfcc-club-store-reset',
		shouldRun: ({ currentPath }): boolean => !isDfccRoute(currentPath),
		run: (): void => {
			useDFCCClubFilterStore.getState().resetDfccClubFilterStore();
		},
	},
	{
		id: 'select-club-member-search-reset',
		shouldRun: ({ currentPath }): boolean => !isSelectClubMemberRoute(currentPath),
		run: (): void => {
			useSelectClubMemberFilterStore.getState().resetSelectClubMemberFilterStore();
		},
	},
];
